import { PortableText, PortableTextProps } from '@portabletext/react'
import { useId, useRef } from 'react'
import { AtButton, AtButtonProps } from '../../atoms'
import { AtButtonDimensions, AtButtonTypes } from '../../types'
import { cn, SizeableHTMLElement, useElementSize } from '../../utils'
import { Image } from '../ml-images'
import { PopupContainer } from './common'

export interface MlMapCardProps {
  image: Image
  title: string
  address: PortableTextProps['value']
  button: Omit<AtButtonProps, 'size' | 'type'>
}

export const MlMapCard = ({ image, title, address, button }: MlMapCardProps) => {
  const addressRef = useRef<SizeableHTMLElement>(null)
  const portableTextSize = useElementSize(addressRef)

  const titleId = useId()
  const descriptionId = useId()

  return (
    <PopupContainer
      className="group flex flex-col items-stretch text-neutrals-white"
      labeledBy={titleId}
      describedBy={descriptionId}
    >
      <img
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        className="flex-1 min-h-0 object-cover"
      />
      <div className="flex flex-col items-stretch m-2.5 mt-3.5">
        <p id={titleId} className="mb-2.5 font-heading font-medium text-[14.45px] leading-[18.78px]">
          {title}
        </p>
        <div
          id={descriptionId}
          // The typings for the style prop are too restrictive and don't
          // allow setting variables, even though it's 100% supported
          //
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          style={portableTextSize ? { '--content-height': `${portableTextSize.height}px` } : null}
          className={cn(
            'max-h-[var(--content-height)] mb-3.5 text-[9.63px] leading-[14.45px]',
            'group-hover:max-h-0 group-hover:opacity-[0] group-hover:mb-1 group-hover:select-none',
            '[transition:_max-height_300ms_ease,_margin_300ms_ease,_opacity_100ms_ease_50ms] motion-reduce:transition-none'
          )}
        >
          <div
            ref={(newAddressRef) => {
              addressRef.current = newAddressRef
            }}
          >
            <PortableText value={address} />
          </div>
        </div>
        <AtButton
          {...button}
          className="z-[1]"
          size={AtButtonDimensions.SMALLER}
          type={AtButtonTypes.PRIMARY}
          parentName="MlMapCard"
        />
      </div>
    </PopupContainer>
  )
}

export * from './skeleton'
